// Time / date formatting helpers for two timezones:
//   • Pittsburgh  (America/New_York)
//   • Moscow      (Europe/Moscow)
//
// Two distinct output formats are used in the app:
//
//   1) Click-to-flip time card  (used inside bubbles on click):
//        "Pittsburgh: 7:25 PM (19:25)"
//        "Moscow:     2:25 AM (02:25)"
//      Uppercase AM/PM with a space.
//
//   2) Date-change separator   (inserted between posts when the day rolls
//      over in either tz):
//        "Pittsburgh: 5:02 PM (17:02) Sun, May, 31, 2026"
//        "Moscow:     12:02 AM (00:02) Пн, Июн, 1, 2026"
//      Both lines use uppercase AM/PM with a space, identical format.
//      Moscow date parts are written in 3-letter Russian abbreviations,
//      but the word "Moscow" itself stays in English (per spec).

const EN_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const EN_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const RU_DAYS = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
const RU_MONTHS = [
  'Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн',
  'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек',
];

const WEEKDAY_INDEX = {
  Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6,
};

function dateParts(date, tz) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    weekday: 'long',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date);
  const get = (t) => parts.find((p) => p.type === t)?.value;
  return {
    year: Number(get('year')),
    month: Number(get('month')) - 1, // 0-indexed
    day: Number(get('day')),
    weekday: WEEKDAY_INDEX[get('weekday')] ?? 0,
  };
}

function timeStrings(date, tz) {
  const h12 = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
  const h24 = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
  return { h12, h24 };
}

// ---------------------------------------------------------------------------
// Click-to-flip format (no date)
// ---------------------------------------------------------------------------

export function formatPittsburgh(date) {
  const { h12, h24 } = timeStrings(date, 'America/New_York');
  return `Pittsburgh: ${h12} (${h24})`;
}

export function formatMoscow(date) {
  const { h12, h24 } = timeStrings(date, 'Europe/Moscow');
  return `Moscow: ${h12} (${h24})`;
}

export function timeLinesForPost(post) {
  const date = new Date(post.created_at);
  const topLang = post.original_lang || post.audio?.language || 'en';
  const pgh = formatPittsburgh(date);
  const msk = formatMoscow(date);
  if (topLang === 'ru') return [msk, pgh];
  return [pgh, msk];
}

// ---------------------------------------------------------------------------
// Date-change separator format (with full date)
// ---------------------------------------------------------------------------

export function formatSeparatorPittsburgh(date) {
  // "Pittsburgh: 5:02 PM (17:02) Sun, May, 31, 2026"
  // Uppercase AM/PM with a space (matches Moscow line for consistency).
  const tz = 'America/New_York';
  const { h12, h24 } = timeStrings(date, tz);
  const { weekday, month, day, year } = dateParts(date, tz);
  return `Pittsburgh: ${h12} (${h24}) ${EN_DAYS[weekday]}, ${EN_MONTHS[month]}, ${day}, ${year}`;
}

export function formatSeparatorMoscow(date) {
  // "Moscow: 12:02 AM (00:02) Пн, Июн, 1, 2026"
  const tz = 'Europe/Moscow';
  const { h12, h24 } = timeStrings(date, tz);
  const { weekday, month, day, year } = dateParts(date, tz);
  return `Moscow: ${h12} (${h24}) ${RU_DAYS[weekday]}, ${RU_MONTHS[month]}, ${day}, ${year}`;
}

// ---------------------------------------------------------------------------
// Detect when the date changes between two posts (in either timezone).
// Returns:
//   • null            — no change in either timezone
//   • 'pittsburgh'    — only Pittsburgh changed, OR both changed and
//                       Pittsburgh's most-recent midnight is the more recent
//   • 'moscow'        — only Moscow changed, OR both changed and Moscow's
//                       most-recent midnight is the more recent
//
// "More recent midnight" is defined as: between the two posts, which
// timezone crossed midnight CLOSEST to the new post's time.  We compute
// this by comparing minutes-since-midnight in each tz at the new post's
// timestamp — whichever is smaller has the more recent midnight (because
// fewer minutes have elapsed since it rolled over).
// ---------------------------------------------------------------------------

function dateKey(date, tz) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

function minutesSinceMidnight(date, tz) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const h = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
  // iOS rendering of hour: 'numeric' with hour12:false can return "24" for
  // midnight in some locales — normalise to 0.
  const hh = h === 24 ? 0 : h;
  const m = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
  return hh * 60 + m;
}

export function whichDateChanged(prevPost, currPost) {
  const prev = new Date(prevPost.created_at);
  const curr = new Date(currPost.created_at);
  const pghChanged = dateKey(prev, 'America/New_York') !== dateKey(curr, 'America/New_York');
  const mskChanged = dateKey(prev, 'Europe/Moscow') !== dateKey(curr, 'Europe/Moscow');
  if (!pghChanged && !mskChanged) return null;
  if (pghChanged && mskChanged) {
    // Both rolled over — whichever has fewer minutes since midnight at the
    // new post's time is the one that "just" turned midnight, i.e. the one
    // that should appear FIRST in the separator.
    const pghMin = minutesSinceMidnight(curr, 'America/New_York');
    const mskMin = minutesSinceMidnight(curr, 'Europe/Moscow');
    return pghMin < mskMin ? 'pittsburgh' : 'moscow';
  }
  return mskChanged ? 'moscow' : 'pittsburgh';
}

// Same most-recent-midnight rule applied to a SINGLE post (used for the very
// first separator in the feed, where there is no previous post to compare).
export function mostRecentMidnight(post) {
  const date = new Date(post.created_at);
  const pghMin = minutesSinceMidnight(date, 'America/New_York');
  const mskMin = minutesSinceMidnight(date, 'Europe/Moscow');
  return pghMin < mskMin ? 'pittsburgh' : 'moscow';
}
